import { Reference } from "@/services/backend/documents/types/reference-number";
import { DocumentStateBase } from "@/services/backend/documents/types/state";
import { UUID } from "@bitory-gmbh/nidavellir";
import { Artefact, Document } from "@bitory-gmbh/bsm";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import { Common } from "@/services/backend/documents/types/common";
import { VDI6022 } from "@/services/backend/htz/report/types/report";

/**
 * Summary combines a set of reports. A customer typically receives
 * several reports at once; the summary gives an overview over all of
 * them and lets the user address the customer in a cover letter.
 */
export interface Summary
  extends Document, Artefact, DocumentStateBase, Common, Reference {
  title: string;
  subtitle: string;
  date?: string;
  workOrderId: UUID;
  coverLetter: string;
  postalAddress: Address;
  serviceAddress: Address;
  representative: Representative;
  items: Item[];
}

export interface Item {
  id: UUID;
  summaryId: UUID;
  reportId: UUID;
  order: number;
  airHandlingUnitName: string;
  vdi6022Result: VDI6022;
  needForAction: NeedForAction;
  recommendations: string;
  createdAt: string;
  updatedAt: string;
}

export const NeedForAction = {
  Zero: "",
  Immediate: "immediate",
  InCycle: "inCycle",
} as const;

export type NeedForAction = (typeof NeedForAction)[keyof typeof NeedForAction];

/* Helper for optimistic updates to a Summary. No validation and no
 * update logic on purpose — the update is done by the backend. */
export function patchSummaryItem(
  summary: Summary,
  itemId: UUID,
  patch: Partial<Item>,
): Summary {
  const items = summary.items.map((i) =>
    i.id === itemId ? { ...i, ...patch } : i,
  );
  return { ...summary, items };
}

/* Helper for optimistic updates to a Summary. Moves an item to a new
 * position and renumbers the order fields, mirroring what the backend
 * does when an item is patched with a new order. */
export function moveSummaryItem(
  summary: Summary,
  fromIndex: number,
  toIndex: number,
): Summary {
  const items = [...summary.items];
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
  return {
    ...summary,
    items: items.map((item, order) => ({ ...item, order })),
  };
}
